import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useSessao } from '../sessao';
import { rodandoComoApp } from '../instalar';
import { Campo, ErroDaApi } from '../ui';

/**
 * A marca na tela de entrada.
 *
 * Duas regras, as mesmas do gerador de PDF:
 *
 * **Nunca uma marca aproximada.** Se o arquivo não estiver lá, a imagem some
 * e o título assume. Melhor sem logo do que com uma logo que não é a da
 * Testesolo.
 *
 * **Nunca recolorir a marca.** No escuro, se existir `marca-branca.png` — a
 * versão negativa que a Testesolo tenha no manual — ela é usada. Não
 * existindo, a colorida ganha de volta o fundo claro para o qual foi
 * desenhada (a chapa creme da regra `.marca-login`), em vez de ser invertida
 * por código. Inverter a silhueta transforma o símbolo num bloco sólido: o
 * "T" e a broca somem, porque eram definidos por contraste de cor.
 *
 * Basta colocar `marca/logo-testesolo-branca.png` no repositório para o
 * `prebuild` publicá-la e esta tela passar a usá-la — sem tocar em código.
 */
export function Marca() {
  /*
   * As duas versões oficiais, e o TEMA escolhe (onda 211). Até a 210 a
   * escolha era do sistema operacional (`matchMedia`), e quem tinha o
   * sistema no escuro e escolheu o tema claro no aplicativo recebia a marca
   * errada. Agora as duas imagens estão na página e os tokens
   * `--ts-marca-*` mostram uma e escondem a outra — a mesma regra do
   * cabeçalho.
   */
  const [sumiu, setSumiu] = useState(false);
  if (sumiu) return null;
  return (
    <div className="marca-login">
      <img className="marca-cor" src="/marca.png"
        alt="Testesolo — Engenharia geotécnica" onError={() => setSumiu(true)} />
      <img className="marca-branca" src="/marca-branca.png"
        alt="Testesolo — Engenharia geotécnica"
        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
    </div>
  );
}

export function Entrar() {
  const { entrar, eu } = useSessao();
  const navegar = useNavigate();
  const local = useLocation();
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [entrando, setEntrando] = useState(false);

  const destino = (local.state as { de?: string } | null)?.de ?? '/';
  if (eu) return <Navigate to={destino} replace />;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEntrando(true);
    try {
      await entrar(usuario.trim(), senha);
      navegar(destino, { replace: true });
    } catch (err) {
      setErro(err);
      // A senha some do campo, o usuário fica: quem errou a senha não quer
      // digitar tudo de novo, e senha na tela é senha por cima do ombro.
      setSenha('');
    } finally {
      setEntrando(false);
    }
  }

  return (
    <div className="entrada dupla">
      <div className="entrada-form">
        <Marca />
        {/*
          * "Labmanager" é o nome comercial do sistema (decisão da Testesolo,
          * 22/08/2026); o nome da empresa está na marca, logo acima.
          */}
        <h1 className="entrada-titulo">Bem-vindo de volta</h1>
        <p className="sub entrada-sub">
          Entre para acompanhar as amostras, os ensaios e os laudos do
          laboratório.
        </p>

        <form className="cartao" onSubmit={enviar} noValidate>
          <ErroDaApi erro={erro} />
          {/*
            * `type="text"`, e NÃO `type="email"`.
            *
            * Desde a 0052 a identidade da conta é o login `nome.sobrenome`,
            * porque o laboratório tem técnico e auxiliar sem caixa
            * corporativa. Com `type="email"` o próprio navegador recusaria
            * a enviar `joao.silva` — a validação nativa exige arroba, e a
            * pessoa ficaria olhando um balão em inglês sem entender por
            * quê. O campo aceita os dois; quem distingue é o banco.
            */}
          <Campo
            rotulo="Usuário"
            type="text"
            value={usuario}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            dica="Seu login (nome.sobrenome) ou o seu e-mail."
            onChange={(e) => setUsuario(e.target.value)}
          />
          <Campo
            rotulo="Senha"
            type="password"
            value={senha}
            autoComplete="current-password"
            required
            onChange={(e) => setSenha(e.target.value)}
          />
          <button type="submit" className="botao largo grande" disabled={entrando}>
            {entrando ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        <p className="sub" style={{ textAlign: 'center' }}>
          Esqueceu a senha? Peça a redefinição a um administrador do sistema.
        </p>
        {/*
          * 298 · o app no celular: quem abre a entrada no navegador do
          * celular encontra aqui a porta da instalação. Dentro do
          * aplicativo instalado o link não aparece — já está instalado.
          */}
        {!rodandoComoApp() && (
          <p className="sub entrada-instalar">
            <Link to="/instalar">Instalar o LabManager ou o Campo no celular</Link>
          </p>
        )}
      </div>

      <PainelDaMarca />
    </div>
  );
}

/* ── O painel ao lado do formulário ─────────────────────────────────── */

/**
 * As frases do painel.
 *
 * A de Terzaghi é a de 1936, na primeira conferência internacional de
 * mecânica dos solos (Harvard) — «Unfortunately, soils are made by nature
 * and not by man, and the products of nature are always complex». As
 * outras duas são da casa: uma sobre o que a rastreabilidade sustenta, a
 * outra sobre o par QA/QC. Nenhuma frase é atribuída a quem não a disse.
 */
const FRASES: { texto: string; quem: string; de: string }[] = [
  {
    texto: 'Infelizmente, os solos são feitos pela natureza, e não pelo homem — '
      + 'e os produtos da natureza são sempre complexos.',
    quem: 'Karl Terzaghi',
    de: 'Conferência Internacional de Mecânica dos Solos, Harvard, 1936',
  },
  {
    texto: 'Um resultado vale o que a sua rastreabilidade sustenta: amostra, '
      + 'equipamento, calibração interna e assinatura. Cada número do laudo diz de onde veio.',
    quem: 'Sistema de Gestão da Qualidade',
    de: 'Testesolo Engenharia Geotécnica',
  },
  {
    texto: 'O controle da qualidade encontra o desvio. A garantia da qualidade '
      + 'faz com que ele não volte.',
    quem: 'QA/QC do laboratório',
    de: 'Laboratório de Geotecnia · Testesolo',
  },
];

/**
 * Os selos.
 *
 * A regra: eles descrevem **o que o sistema faz**, nunca o que o
 * laboratório é.
 *
 * A lista anterior abria com «ABNT NBR ISO/IEC 17025» e trazia
 * «Rastreabilidade metrológica». Os dois leem como declaração de
 * acreditação numa tela pública, e a Testesolo **não é acreditada**
 * (confirmado em 08/09/2026). A diferença não é retórica: esta lista
 * qualquer pessoa confere abrindo o sistema; a outra exigiria um
 * certificado emitido por organismo acreditador.
 *
 * Se a acreditação sair, o selo volta COM o número do certificado ao
 * lado — é assim que uma acreditação se apresenta, e sem o número ela
 * parece alegação mesmo quando é verdadeira.
 */
const SELOS = [
  'Trilha de auditoria em cada registro',
  'Calibração interna do equipamento registrada',
  'Norma vigente citada no ensaio',
  'Laudo com QR de autenticidade',
];

function PainelDaMarca() {
  const [i, setI] = useState(0);
  const [parado, setParado] = useState(false);

  /*
   * Troca sozinha a cada 8 s, para de trocar com o mouse em cima, e não
   * troca nunca para quem pediu menos movimento ao sistema — as bolinhas
   * continuam lá para trocar à mão.
   */
  useEffect(() => {
    const menos = typeof matchMedia === 'function'
      && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (parado || menos) return;
    const t = setInterval(() => setI((x) => (x + 1) % FRASES.length), 8000);
    return () => clearInterval(t);
  }, [parado]);

  const f = FRASES[i];
  return (
    <aside className="entrada-painel" aria-label="Sobre o Labmanager"
      onMouseEnter={() => setParado(true)} onMouseLeave={() => setParado(false)}>
      <div className="painel-topo">
        <p className="painel-eyebrow">Labmanager</p>
        <h2 className="painel-titulo">
          O gLIMS da Testesolo
        </h2>
        {/*
          * A sigla passa a se explicar.
          *
          * O título dizia «O gLIMS da Testesolo» e nada dizia o que é um
          * gLIMS: quem entra pela primeira vez — cliente, auditor, técnico
          * novo — lia uma sigla. Duas frases resolvem, e cabem no espaço
          * que o selo de acreditação desocupou.
          */}
        <p className="painel-sub">
          gLIMS é o sistema que administra um laboratório geotécnico. Aqui,
          cada amostra tem o caminho inteiro registrado — do recebimento ao
          laudo assinado, com quem executou, em qual equipamento e sob qual
          norma.
        </p>
      </div>

      <figure className="painel-frase" key={i} aria-live="polite">
        <span className="aspas" aria-hidden="true">“</span>
        <blockquote>{f.texto}</blockquote>
        <figcaption>
          <b>{f.quem}</b>
          <span>{f.de}</span>
        </figcaption>
      </figure>
      <div className="painel-pontos" role="tablist" aria-label="Frases">
        {FRASES.map((x, k) => (
          <button key={x.quem} type="button" role="tab"
            aria-selected={k === i} aria-label={`Frase ${k + 1}: ${x.quem}`}
            className={k === i ? 'ativo' : undefined}
            onClick={() => setI(k)} />
        ))}
      </div>

      <div className="painel-selos">
        <span className="rot">O que o sistema garante</span>
        <ul>
          {SELOS.map((s) => <li key={s}>{s}</li>)}
        </ul>
      </div>
    </aside>
  );
}
